/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();

import { Loader2, Video } from 'lucide-react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import React, { forwardRef, useEffect, useState } from 'react'

import { useCarousel } from '@/components/e-commerce/kits/kit-gallery-carousel/Carousel'
import { fetchVimeoMetadata, VimeoMetadata } from '@/lib/vimeo/metadata'
import { cn } from '@/utils/css'

/** A description of the available properties for this component. */
type CarouselVimeoThumbnailProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  /** The index of the thumbnail within the carousel. */
  index: number

  /** The Vimeo video ID to display the thumbnail for. */
  videoId: string
}

/**
 * A thumbnail for the carousel that points towards a Vimeo video. This component communicates with the Vimeo API to
 * obtain the thumbnail for the video, and displays it with a 'play' icon overlay on top to indicate that the
 * thumbnail is a video.
 */
const CarouselVimeoThumbnail = forwardRef<HTMLButtonElement, CarouselVimeoThumbnailProps>(
  ({ index, videoId, className, ...props }, ref) => {
    const t = useTranslations('CarouselThumbnail')
    const { selectedIndex, thumbnailOnClick } = useCarousel()
    const [vimeoMetadata, setVimeoMetadata] = useState<VimeoMetadata | null>(null)

    useEffect(() => {
      if (!vimeoMetadata) {
        fetchVimeoMetadata(videoId).then((data) => setVimeoMetadata(data))
      }
    }, [vimeoMetadata, videoId])

    return (
      <button
        type={'button'}
        ref={ref}
        aria-label={t('selectVideo', { index: index + 1 })}
        className={cn(
          'relative cursor-pointer rounded-2xl overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:ring-offset-2',
          index === selectedIndex && 'border-2 border-blue-500',
          index !== selectedIndex && 'border border-grey-50/20',
          className,
        )}
        onClick={() => thumbnailOnClick(index)}
        {...props}
      >
        <div
          className={'absolute right-2 top-2 z-10 p-1 bg-primary-500/60 rounded-full flex items-center justify-center'}
        >
          <Video aria-hidden={'true'} className={'w-6 h-6 text-white'} />
        </div>
        {!vimeoMetadata && (
          <div className={'absolute inset-0 flex items-center justify-center'}>
            <Loader2 aria-hidden={'true'} className={'animate-spin'} size={14} />
          </div>
        )}
        {vimeoMetadata && <Image src={vimeoMetadata.thumbnail_medium} alt={''} fill className={'object-cover'} />}
      </button>
    )
  },
)
CarouselVimeoThumbnail.displayName = 'CarouselVimeoThumbnail'

export { CarouselVimeoThumbnail }
